import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  User,
  ChevronRight,
  Shield,
  Building2,
  Code2,
  Ship,
  QrCode,
  KeyRound
} from 'lucide-react';

import jaydevsprlogo from './assets/jay_logo-whitebg.png';
import { ReactComponent as PrLocalLogo } from './assets/white-pr-local-logo-no-bg.svg';
import navelaLogo from '../Navela_components/assets/navela-logo-dark.png';
import autoleyIcon from '../AutoLey_components/assets/autoley-icon.png';

export default function AdminPortal() {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  // --- Dashboard Configuration ---
  const dashboards = [
    {
      id: 'prlocal',
      name: 'PR LocaL',
      description: 'Gestión y administración de la plataforma PR LocaL (Negocios, Reportes, Ubicaciones)',
      iconType: 'svg',
      iconSrc: PrLocalLogo, 
      fallbackIcon: Building2,
      gradient: 'from-indigo-500 to-violet-700',
      hoverGradient: 'from-indigo-600 to-violet-800',
      bgColor: 'bg-violet-50',
      borderColor: 'border-violet-200',
      iconColor: 'text-violet-600',
      path: '/prlocal/admin-dashboard',
      badge: 'accesible' //Producción
    },
    {
      id: 'jaydevspr',
      name: 'JaydevsPR',
      description: 'Panel de control principal de JaydevsPR (Usuarios, Accesos, Logs)',
      iconType: 'img',
      iconSrc: jaydevsprlogo,
      fallbackIcon: Code2,
      gradient: 'from-orange-500 to-orange-700',
      hoverGradient: 'from-orange-600 to-orange-800',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      iconColor: 'text-orange-600',
      path: '/jaydevspr/admin-dashboard',
      badge: 'Admin'
    },
    {
      id: 'navela',
      name: 'Navela',
      description: 'Administración de la flota Navela (Embarcaciones, Rutas de barcos e Imágenes)',
      iconType: 'img',
      iconSrc: navelaLogo,
      fallbackIcon: Ship,
      gradient: 'from-pr-navy-500 to-pr-navy-700',
      hoverGradient: 'from-pr-navy-600 to-pr-navy-800',
      bgColor: 'bg-pr-navy-50',
      borderColor: 'border-pr-navy-200',
      iconColor: 'text-pr-navy-600',
      path: '/navela/admin-dashboard',
      badge: 'Fleet'
    },
    {
      id: 'qrcreate',
      name: 'QR Create',
      description: 'Administración de QR Create (Usuarios, Créditos, Códigos QR, Escaneos y Suscripciones)',
      iconType: 'icon',
      iconSrc: null,
      fallbackIcon: QrCode,
      gradient: 'from-sky-500 to-sky-700',
      hoverGradient: 'from-sky-600 to-sky-800',
      bgColor: 'bg-sky-50',
      borderColor: 'border-sky-200',
      iconColor: 'text-sky-600',
      path: '/qrcreate/admin-dashboard',
      badge: 'Operator'
    },
    {
      id: 'autoley',
      name: 'AutoLey',
      description: 'Licencias de AutoLey (Activaciones por computadora, instalaciones duplicadas y activaciones adicionales)',
      iconType: 'img',
      iconSrc: autoleyIcon,
      fallbackIcon: KeyRound,
      gradient: 'from-blue-700 to-blue-950',
      hoverGradient: 'from-blue-800 to-blue-950',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-700',
      path: '/autoley/admin-dashboard',
      badge: 'Licencias'
    }
  ];

  const handleDashboardSelect = (dashboard) => {
    navigate(dashboard.path);
  };

  const handleLogout = () => {
    // For now, just navigate to home
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex flex-col">
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          
          {/* Logo Area */}
          <div className="flex items-center space-x-3">
            <div className="bg-slate-900 p-2 rounded-lg shadow-sm">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800 leading-none">
                JaydevsPR
              </h1>
              <p className="text-xs text-slate-500 font-medium">Portal Central</p>
            </div>
          </div>
          
          {/* User Actions */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="hidden sm:flex items-center space-x-3 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
              <div className="bg-white p-1 rounded-full shadow-sm">
                <User className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-sm font-medium text-slate-700 pr-2">
                Administrador
              </span>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-red-600 hover:border-red-200 transition-all duration-200 shadow-sm"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline font-medium">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        
        <div className="text-center mb-12 max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700">
          <h2 className="text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
            Panel de Administración
          </h2>
          <p className="text-lg text-slate-600">
            Seleccione el entorno de trabajo al que desea acceder para gestionar sus plataformas.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl w-full mx-auto">
          {dashboards.map((dashboard) => {
            const isHovered = hoveredCard === dashboard.id;
            const FallbackIcon = dashboard.fallbackIcon;

            return (
              <button
                key={dashboard.id}
                onClick={() => handleDashboardSelect(dashboard)}
                onMouseEnter={() => setHoveredCard(dashboard.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`group relative bg-white rounded-3xl shadow-xl border-2 ${isHovered ? 'border-transparent' : dashboard.borderColor} hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 text-left overflow-hidden h-full flex flex-col`}
              >
                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${dashboard.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0`} />
                
                {/* Content Container */}
                <div className="relative z-10 p-8 flex flex-col h-full">
                  
                  {/* Icon & Badge Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${dashboard.gradient} flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/20 transition-all duration-500`}>
                        {dashboard.iconType === 'img' ? (
                            <img src={dashboard.iconSrc} alt={dashboard.name} className="h-14 w-14 object-contain drop-shadow-md" />
                        ) : dashboard.iconType === 'svg' && dashboard.iconSrc ? (
                            <dashboard.iconSrc className="h-14 w-14 text-white drop-shadow-md" />
                        ) : (
                            <FallbackIcon className="h-10 w-10 text-white" />
                        )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${isHovered ? 'bg-white/20 text-white border-white/40' : `${dashboard.bgColor} ${dashboard.iconColor} ${dashboard.borderColor}`}`}>
                      {dashboard.badge}
                    </span>
                  </div>

                  {/* Text Content */}
                  <h3 className={`text-3xl font-bold mb-3 transition-colors duration-300 ${isHovered ? 'text-white' : 'text-slate-800'}`}>
                    {dashboard.name}
                  </h3>
                  <p className={`text-base mb-8 flex-grow transition-colors duration-300 ${isHovered ? 'text-white/90' : 'text-slate-500'}`}>
                    {dashboard.description}
                  </p>

                  {/* Action Button Visual */}
                  <div className={`mt-auto flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${isHovered ? 'bg-white/20 text-white' : 'bg-slate-50 text-slate-600 group-hover:bg-slate-100'}`}>
                    <span className="font-semibold">Acceder al Dashboard</span>
                    <div className={`p-1 rounded-full ${isHovered ? 'bg-white text-current' : 'bg-white shadow-sm'}`}>
                        <ChevronRight className={`h-5 w-5 transform group-hover:translate-x-0.5 transition-transform ${isHovered ? `text-${dashboard.gradient.split('-')[1]}-600` : 'text-slate-400'}`} />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="mt-16 text-center animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-100">
          <p className="text-xs text-slate-400 font-medium uppercase tracking-widest mb-2">
            Sistema Seguro de Administración
          </p>
          <p className="text-xs text-slate-300">
            © 2024 JaydevsPR. Todos los derechos reservados.
          </p>
        </div>
      </main>
    </div>
  );
}